import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiHelper } from '../ApiHelper';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { Label } from '@fluentui/react/lib/Label';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { Separator } from '@fluentui/react/lib/Separator';
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { ProfileTypes, ProfileList, TimelineList, ProfileItem } from './DailyApiProfiles.tsx';
import '../styles/common.css';

interface AdhocApiProfileResponse {
    id: string;
    userAlias: string;
    createdDate: string;
    date: string;
    farm: string;
    api: string;
    maxRequestCount: number;
    successOnly: boolean;
    kqlFilter?: string;
    status: number; // 0=Created, 1=InProgress, 2=Succeeded, 3=Failed
    progress: number;
    error?: string;
}

const StatusNames: Record<number, string> = {
    0: 'Created',
    1: 'In Progress',
    2: 'Succeeded',
    3: 'Failed',
    4: 'Farm Not Supported',
    5: 'Bad Query',
    6: 'Too Few Requests',
    7: 'No Profiled Requests',
    8: 'Partial Success',
    9: 'No Machine Profiles'
};

const StatusColors: Record<number, string> = {
    0: '#605e5c', // Gray - Created
    1: '#0078d4', // Blue - In Progress
    2: '#107c10', // Green - Succeeded
    3: '#d83b01', // Red - Failed
    4: '#d83b01', // Red - Farm Not Supported
    5: '#d83b01', // Red - Bad Query
    6: '#e97548', // Orange - Too Few Requests
    7: '#d83b01', // Red - No Profiled Requests
    8: '#ca5010', // Orange - Partial Success
    9: '#e97548'  // Orange - No Machine Profiles
};

export function AdhocProfile() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [profile, setProfile] = useState<AdhocApiProfileResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

    const fetchProfile = async () => {
        try {
            setError(null);
            const response = await ApiHelper<AdhocApiProfileResponse>(`/api/v1/adhoc-api-profiles/${id}`, 'GET');
            
            if (response && response.status < 10) {
                setProfile(response);
            } else {
                setError('Failed to load profile:' + response?.status);
                setProfile(null);
            }
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError('An error occurred while loading the profile');
            setProfile(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchProfile();
        }
    }, [id]);

    // Set up auto-refresh for Created/InProgress status
    useEffect(() => {
        if (profile) {
            window.document.title = 'Adhoc profile: ' + profile.api
                + ' from ' + profile.farm + ' on ' + formatApiDate(profile.date);
        }

        if (profile?.status === 1 || profile?.status === 0) { 
            const interval = setInterval(() => {
                fetchProfile();
            }, 10000); // Refresh every 10 seconds
            setRefreshInterval(interval);
        } else {
            // Clear any existing interval when status is not InProgress
            if (refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }
        }

        // Cleanup on unmount
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [profile?.status]);


    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatApiDate = (dateString: string) => {
        return new Date(dateString).toISOString().split('T')[0];
    };

    if (!id) {
        return (
            <div style={{ margin: '20px' }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    No profile ID provided
                </MessageBar>
            </div>
        );
    }

    return (
        <div style={{ margin: '20px' }}>
            <Stack tokens={{ childrenGap: 20 }}>
                
                {!loading && profile && (
                    <Text variant="xxLarge" as="h1">
                        {profile.api}<br></br>
                        from {profile.farm} on {formatApiDate(profile.date)}
                    </Text>
                )}

                {loading && (
                    <Spinner 
                        size={SpinnerSize.large} 
                        label="Loading profile..." 
                    />
                )}

                {error && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {error}
                    </MessageBar>
                )}

                {profile && !loading && (
                    <Stack tokens={{ childrenGap: 15 }}>
                        {/* Auto-refresh indicator for InProgress status */}
                        {(profile.status === 1 || profile.status === 0) && (
                            <MessageBar messageBarType={MessageBarType.info}>
                                Profile is in progress. This page will refresh automatically every 10 seconds.
                            </MessageBar>
                        )}

                        {/* Profile Header */}
                        {profile && profile.status != 2 && (
                            <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
                                <Text variant="xLarge" style={{ color: StatusColors[profile.status] }}>
                                    Status: {StatusNames[profile.status]}
                                </Text>
                                {profile.status === 1 && (
                                    <ProgressIndicator
                                        percentComplete={profile.progress / 100}
                                        description={`${profile.progress}% complete`}
                                        styles={{ root: { width: 200 } }}
                                    />
                                )}
                            </Stack>)}

                        {/* Error Display - Show for status >= 3 (except PartialSuccess) */}
                        {profile.status >= 3 && profile.status !== 8 && (
                            <MessageBar
                                messageBarType={
                                    profile.status === 6 || profile.status === 9
                                        ? MessageBarType.warning
                                        : MessageBarType.error
                                }
                            >
                                <strong>Profile Generation {profile.status === 3 ? 'Failed' : 'Issue'}</strong><br />
                                {profile.error || 'An error occurred during profile generation.'}
                            </MessageBar>
                        )}

                        {/* Partial Success Display */}
                        {profile.status === 8 && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                <strong>Partial Success</strong><br />
                                {profile.error || 'Profile generated with warnings.'}
                            </MessageBar>
                        )}

                        <Separator />


                        {/* Configuration Details */}
                        <Stack tokens={{ childrenGap: 10 }}>
                            
                            <Stack horizontal tokens={{ childrenGap: 40 }}>
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <Label>Max Request Count</Label>
                                    <Text variant="medium">{profile.maxRequestCount.toLocaleString()}</Text>
                                </Stack>
                                
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <Label>Success Only</Label>
                                    <Text variant="medium">{profile.successOnly ? 'Yes' : 'No'}</Text>
                                </Stack>
                            </Stack>

                            {profile.kqlFilter && (
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <Label>KQL Filter</Label>
                                    <Text variant="medium" style={{ 
                                        fontFamily: 'monospace', 
                                        background: '#f3f2f1', 
                                        padding: '8px',
                                        borderRadius: '4px',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {profile.kqlFilter}
                                    </Text>
                                </Stack>
                            )}
                        </Stack>

                        <Separator />

                        {/* Profile Results - Show when successful or partial success */}
                        {(profile.status === 2 || profile.status === 8) && (
                            <Stack tokens={{ childrenGap: 10 }}>
                                <Text variant="large" as="h2">Profile Results</Text>

                                <ProfileList path={profile.id} updateState={() => {}} />

                                <TimelineList path={profile.id} />
                            </Stack>
                        )}

                        <Separator />

                        {/* Action Buttons */}
                        {profile && profile.status < 2 && (
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                <PrimaryButton
                                    text="Refresh Now"
                                    iconProps={{ iconName: 'Refresh' }}
                                    onClick={() => {
                                        setLoading(true);
                                        fetchProfile();
                                    }}
                                    disabled={loading}
                                />
                            </Stack>)}
                    </Stack>
                )}
            </Stack>
        </div>
    );
}